/**
 * Push through the official mechanism on each platform, behind one client-side
 * surface, and a notification tap that deep-links into the app,
 * including from a cold start.
 *
 * Shaped like `serviceWorkerState()` in features/diagnostics and for the same reason:
 * probe, return a discriminated string, never throw. Every call in here can fail
 * in a way that is a *result to report* rather than a crash — Firefox with push
 * disabled, an Android build with no Play Services, an iOS webview with no
 * Notification API at all. A thrown error would take the diagnostics screen down
 * with it, and that screen is where these results are read.
 */
import { api } from '../../api'
import { META_KEYS, metaGet, metaSet } from '../../db/db'
import { BUILD_TARGET } from '../../shared/config'

/** Twin of the constant in public/push-sw.js. Changing one means changing both. */
const DEEP_LINK_MESSAGE = 'sciolingo:deep-link'

/**
 * The only place in this bundle that can reach `./push.native`, and therefore
 * `@capacitor/push-notifications`.
 *
 * The guard tests `__BUILD_TARGET__` — the raw Vite `define` — rather than the
 * `BUILD_TARGET` constant re-exported from config.ts. The define is rewritten in
 * place before Rollup runs, so the branch is already `if ('web' !== 'mobile')`
 * and the import below is plainly unreachable. Routing every caller through here
 * matters just as much: the obvious spelling is to import inside
 * `if (transport === 'fcm')`, but `transport` is a runtime value, Rollup cannot
 * prove anything about it, and the plugin is emitted into dist-web as its own
 * chunk. It was — `scripts/check-web-bundle.mjs` caught it, which is what that
 * script is for.
 */
type NativePush = typeof import('./push.native')

async function nativePush(): Promise<NativePush | null> {
  if (__BUILD_TARGET__ !== 'mobile') return null
  return import('./push.native')
}

export type PushTransport = 'webpush' | 'fcm' | 'unsupported'

export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied' | 'prompt-with-rationale'

export interface PushState {
  transport: PushTransport
  permission: PushPermission
  subscribed: boolean
  /** Web: the push endpoint. Native: the FCM token. Truncate at the call site. */
  handle: string | null
  /** Non-null when the probe itself failed. A result, not a throw. */
  error: string | null
}

export interface LastPush {
  body: string
  url: string
  at: number
  via: PushTransport
}

interface StoredPush {
  transport: PushTransport
  handle: string
  vapidPublicKey?: string
  subscribedAt: number
}

/**
 * Synchronous, like `isStandalone()`, because the diagnostics card needs it on
 * first paint.
 *
 * `fcm` means "this build intends to use FCM", not "FCM is working" —
 * `pushState()` answers the latter. The web branch checks all three APIs
 * because all three are independently missing somewhere: iOS Safari below 16.4
 * has no PushManager in a tab, and some embedded webviews expose Notification
 * without ServiceWorker.
 */
export function pushTransport(): PushTransport {
  if (BUILD_TARGET === 'mobile') return 'fcm'
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported'
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  return supported ? 'webpush' : 'unsupported'
}

export async function pushState(): Promise<PushState> {
  const transport = pushTransport()
  if (transport === 'unsupported') {
    return { transport, permission: 'unsupported', subscribed: false, handle: null, error: null }
  }

  try {
    if (transport === 'fcm') {
      const native = await nativePush()
      if (!native) {
        return { transport, permission: 'unsupported', subscribed: false, handle: null, error: 'not a native build' }
      }
      const stored = await metaGet<StoredPush>(META_KEYS.push)
      const token = native.nativeToken() ?? stored?.handle ?? null

      // `subscribed` means "the server holds this handle", not "the device has
      // a token". META_KEYS.push is written only after registerPushSubscription
      // returns, so it is the only local evidence the server ever accepted us.
      // Inferring it from the token instead made a failed registration look
      // exactly like a successful one — the device had a token either way, and
      // the card cheerfully reported success while the registry stayed empty.
      const registered = stored?.handle != null

      return {
        transport,
        permission: (await native.nativePermission()) as PushPermission,
        subscribed: registered,
        handle: token,
        error:
          (await metaGet<string>(META_KEYS.pushError)) ??
          (token !== null && !registered ? 'FCM token obtained, but the server never accepted it' : null),
      }
    }

    const registration = await navigator.serviceWorker.getRegistration()
    const subscription = (await registration?.pushManager.getSubscription()) ?? null

    // A subscription made under an older VAPID key still looks perfectly
    // healthy here — only the push service knows, and only when something is
    // sent. Comparing against what we pinned at subscribe time is the only way
    // the client can notice and re-subscribe itself.
    const stored = await metaGet<StoredPush>(META_KEYS.push)
    const staleKey =
      subscription !== null && stored?.vapidPublicKey !== undefined
        ? await vapidKeyChanged(stored.vapidPublicKey)
        : false

    const registered = stored?.handle != null

    return {
      transport,
      permission: Notification.permission as PushPermission,
      // Same distinction as the native branch: a browser subscription the
      // server never accepted is not a subscription for our purposes.
      subscribed: subscription !== null && registered,
      handle: subscription?.endpoint ?? null,
      error:
        (await metaGet<string>(META_KEYS.pushError)) ??
        (staleKey
          ? 'vapid-key-rotated — subscribe again'
          : subscription !== null && !registered
            ? 'browser subscription exists, but the server never accepted it'
            : null),
    }
  } catch (cause) {
    return { transport, permission: 'unsupported', subscribed: false, handle: null, error: String(cause) }
  }
}

export async function subscribePush(): Promise<PushState> {
  const transport = pushTransport()
  if (transport === 'unsupported') {
    return { transport, permission: 'unsupported', subscribed: false, handle: null, error: null }
  }

  try {
    const state = transport === 'fcm' ? await subscribeNativePush() : await subscribeWebPush()
    // Cleared only on a genuine success, so a stale reason never outlives the
    // problem it describes.
    if (state.subscribed) await metaSet(META_KEYS.pushError, null)
    else if (state.error) await metaSet(META_KEYS.pushError, state.error)
    return state
  } catch (cause) {
    // The registration POST is the most likely thing to throw here, and its
    // failure is the one that leaves the device holding a perfectly good token
    // the server has never heard of. Persisting the reason is what turns that
    // from "nothing happens" into something readable on the card.
    const error = cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)
    await metaSet(META_KEYS.pushError, error)
    return { transport, permission: 'default', subscribed: false, handle: null, error }
  }
}

async function subscribeWebPush(): Promise<PushState> {
  const permission = (await Notification.requestPermission()) as PushPermission
  if (permission !== 'granted') {
    return { transport: 'webpush', permission, subscribed: false, handle: null, error: null }
  }

  // `navigator.serviceWorker.ready` never settles when no worker will ever
  // register — a Firefox private window, or a build where the SW is disabled.
  // A bare await would hang the diagnostics card instead of reporting it.
  const registration = await withTimeout(navigator.serviceWorker.ready, 5_000)
  if (!registration) {
    return {
      transport: 'webpush',
      permission,
      subscribed: false,
      handle: null,
      error: 'service-worker-never-ready',
    }
  }

  const { publicKey } = await api.getVapidPublicKey()

  // An existing subscription made under a different key has to go before a new
  // one can be created — pushManager.subscribe() rejects with InvalidStateError
  // when applicationServerKey differs from the existing one. Tell the server
  // too, or the orphaned row sits in the registry until a send fails and prunes
  // it, showing up as a failure in the delivery report in the meantime.
  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    const staleEndpoint = existing.endpoint
    await existing.unsubscribe()
    await api.unregisterPushSubscription({ endpoint: staleEndpoint }).catch(() => {
      // Best effort. Pruning on 410 is the backstop, and failing here must not
      // stop a re-subscribe that is otherwise about to succeed.
    })
  }

  const subscription = await registration.pushManager.subscribe({
    // Non-negotiable in Chromium: a silent push subscription is refused. It also
    // matches the design — every push here shows a notification.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  const json = subscription.toJSON()
  const ack = await api.registerPushSubscription({
    transport: 'webpush',
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    label: navigator.userAgent.slice(0, 120),
  })

  await metaSet(META_KEYS.push, {
    transport: 'webpush',
    handle: subscription.endpoint,
    vapidPublicKey: ack.vapidPublicKey,
    subscribedAt: Date.now(),
  } satisfies StoredPush)

  return { transport: 'webpush', permission, subscribed: true, handle: subscription.endpoint, error: null }
}

async function subscribeNativePush(): Promise<PushState> {
  const native = await nativePush()
  if (!native) {
    return { transport: 'fcm', permission: 'unsupported', subscribed: false, handle: null, error: 'not a native build' }
  }

  const { token, permission } = await native.subscribeNative()

  if (!token) {
    return {
      transport: 'fcm',
      permission: permission as PushPermission,
      subscribed: false,
      handle: null,
      // Always a reason. Reporting null when permission was refused made the
      // most ordinary failure of all look like nothing having happened.
      error:
        permission === 'granted'
          ? 'no FCM token after 10s — Play Services missing, or google-services.json absent from the APK'
          : `notification permission is "${permission}", not granted`,
    }
  }

  // Not wrapped in a catch: subscribePush() records the reason and the card
  // shows it. Swallowing it here is what produced a device holding a valid
  // token, a card reporting success, and an empty registry on the server.
  const ack = await api.registerPushSubscription({
    transport: 'fcm',
    token,
    label: navigator.userAgent.slice(0, 120),
  })

  if (ack.transport !== 'fcm') {
    throw new Error(`server registered this device as "${ack.transport}", expected "fcm"`)
  }

  await metaSet(META_KEYS.push, {
    transport: 'fcm',
    handle: token,
    subscribedAt: Date.now(),
  } satisfies StoredPush)

  return { transport: 'fcm', permission: 'granted', subscribed: true, handle: token, error: null }
}

export async function unsubscribePush(): Promise<PushState> {
  const transport = pushTransport()

  try {
    if (transport === 'fcm') {
      const stored = await metaGet<StoredPush>(META_KEYS.push)
      const native = await nativePush()
      // Server first: a server row for a dead handle is the worse failure, since
      // it keeps the delivery report showing a permanent error.
      if (stored?.handle) await api.unregisterPushSubscription({ token: stored.handle })
      await native?.unsubscribeNative()
    } else if (transport === 'webpush') {
      const registration = await navigator.serviceWorker.getRegistration()
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) {
        await api.unregisterPushSubscription({ endpoint: subscription.endpoint })
        await subscription.unsubscribe()
      }
    }

    await metaSet(META_KEYS.push, null)
    await metaSet(META_KEYS.pushError, null)
  } catch (cause) {
    return { transport, permission: 'default', subscribed: false, handle: null, error: String(cause) }
  }

  return pushState()
}

export async function lastReceivedPush(): Promise<LastPush | null> {
  return (await metaGet<LastPush>(META_KEYS.lastPush)) ?? null
}

// ---------------------------------------------------------------------------
// Deep links — queue the payload until the router mounts
// ---------------------------------------------------------------------------

/**
 * Two things arrive out of order and neither can be made to wait: a cold-start
 * tap fires before React exists, and on the web it fires in the Service Worker,
 * on another thread, before the page has finished parsing. So the payload is
 * parked here and the React side collects it when it is ready.
 *
 * "Ready" is stricter than "the router mounted". A tap that lands on a logged-out
 * app must not be spent on the login screen, so App.tsx gates the drain on auth
 * and until then the payload simply stays parked.
 */
let pendingDeepLink: string | null = null
const deepLinkListeners = new Set<(url: string) => void>()

export function offerDeepLink(url: string): void {
  if (deepLinkListeners.size === 0) {
    pendingDeepLink = url
    return
  }
  for (const listener of deepLinkListeners) listener(url)
}

export function onDeepLink(listener: (url: string) => void): () => void {
  deepLinkListeners.add(listener)

  if (pendingDeepLink !== null) {
    const url = pendingDeepLink
    pendingDeepLink = null
    // A microtask, not a synchronous call: this runs inside useEffect, and
    // navigating during the effect that subscribes is a state update on a
    // component that is still committing.
    queueMicrotask(() => listener(url))
  }

  return () => deepLinkListeners.delete(listener)
}

/**
 * The URL in a push payload steers the app, so it is treated as untrusted input:
 * a same-origin path, never an absolute URL, never a scheme. The server is
 * trusted to choose *which* session; it is not trusted to choose which origin.
 */
export function toInAppPath(raw: string, origin: string): string | null {
  try {
    const url = new URL(raw, origin)
    if (url.origin !== origin) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

/**
 * Call once at module scope from main.tsx — before React renders. A cold-start
 * notification tap fires before any component exists, so a listener attached in
 * an effect is attached too late and the tap is lost.
 */
export function installPushBridge(): void {
  if (BUILD_TARGET === 'mobile') {
    // Fire and forget: the import must not block first paint, and the listeners
    // it installs are the cold-start path, so it must not wait for React either.
    void nativePush().then((native) => {
      native?.installNativeListeners({
        onDeepLink: (url) => offerDeepLink(url),
        onReceived: (body, url) => void recordReceived(body, url, 'fcm'),
      })
    })
    return
  }

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string; body?: string } | null
      if (data?.type !== DEEP_LINK_MESSAGE || typeof data.url !== 'string') return
      void recordReceived(data.body ?? '', data.url, 'webpush')
      offerDeepLink(data.url)
    })
  }

  // Cold start on the web: the worker called `clients.openWindow()`, so the
  // target is already the document's own location. Rather than letting the
  // router silently do the right thing, push it through the same queue — that
  // way "arrived from a notification" has exactly one code path, and a tap that
  // lands on a logged-out app is honoured after login instead of lost.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    if (params.get('source') === 'push') {
      offerDeepLink(window.location.pathname + window.location.search)
    }
  }
}

async function recordReceived(body: string, url: string, via: PushTransport): Promise<void> {
  await metaSet(META_KEYS.lastPush, { body, url, at: Date.now(), via } satisfies LastPush)
}

async function vapidKeyChanged(pinned: string): Promise<boolean> {
  try {
    const { publicKey } = await api.getVapidPublicKey()
    return publicKey !== pinned
  } catch {
    // Offline is not evidence of rotation.
    return false
  }
}

/**
 * base64url → the `Uint8Array` `applicationServerKey` requires. The browser
 * rejects the string form, so this conversion is mandatory rather than stylistic.
 */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=')
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))

  // Backed by an explicit ArrayBuffer rather than `new Uint8Array(length)`.
  // Since TypeScript 5.7 the latter is typed `Uint8Array<ArrayBufferLike>`,
  // which might be a SharedArrayBuffer and so is not a `BufferSource` —
  // `applicationServerKey` rejects it at compile time.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([promise, new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))])
}
